import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Container, 
  Grid, 
  Paper,
  Snackbar,
  Alert
} from '@mui/material';

const ContactForm = ({
  title = 'Contact Us',
  description = 'Fill out the form below and we\'ll get back to you as soon as possible.',
  submitButtonText = 'Send Message',
  backgroundColor = '#ffffff',
  textColor = '#333333',
  padding = '3rem 1rem',
  maxWidth = '600px',
  accountId,
  microSiteId,
  pageId
}) => {
  console.log('Rendering ContactForm component with props:', { title, accountId, microSiteId, pageId });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Track form submission with analytics
  const trackFormSubmission = async (formData, success) => {
    try {
      // Get the current origin for API calls
      const baseUrl = window.location.origin;
      const apiUrl = `${baseUrl}/api/analytics/track/interaction`;
      
      // Create the payload
      const payload = {
        pageId,
        microSiteId,
        sessionId: localStorage.getItem('analytics_session_id'),
        eventType: 'form_submit',
        elementId: 'contact-form',
        elementType: 'form',
        data: {
          formType: 'contact',
          success,
          hasName: !!formData.name,
          hasEmail: !!formData.email,
          hasSubject: !!formData.subject,
          messageLength: formData.message?.length || 0
        }
      };
      
      // Only include accountId if it exists
      if (accountId) {
        payload.accountId = accountId;
      }
      
      // Send the analytics event
      await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error('Error tracking form submission:', error);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // In a real implementation, you would send this data to your API
      // For demo purposes, we'll just simulate a successful submission
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Track successful form submission
      await trackFormSubmission(formData, true);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Your message has been sent successfully!',
        severity: 'success'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Track failed form submission
      await trackFormSubmission(formData, false);
      
      setSnackbar({
        open: true,
        message: 'There was an error sending your message. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Box
      sx={{
        backgroundColor,
        color: textColor,
        padding,
        width: '100%'
      }}
    >
      <Container maxWidth="lg" sx={{ maxWidth }}>
        {title && (
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="subtitle1" align="center" sx={{ mb: 4 }}>
            {description}
          </Typography>
        )}
        <Paper elevation={1} sx={{ p: 3 }}>
          <form onSubmit={handleSubmit} id="contact-form" data-analytics-id="contact-form">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  id="contact-form-name"
                  data-analytics-id="contact-form-name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  id="contact-form-email"
                  data-analytics-id="contact-form-email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  id="contact-form-subject"
                  data-analytics-id="contact-form-subject"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                  required
                  id="contact-form-message"
                  data-analytics-id="contact-form-message"
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isSubmitting}
                  id="contact-form-submit"
                  data-analytics-id="contact-form-submit"
                >
                  {isSubmitting ? 'Sending...' : submitButtonText}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Define the fields for the Puck editor
ContactForm.fields = {
  title: {
    type: 'text',
    label: 'Title',
    default: 'Contact Us'
  },
  description: {
    type: 'text',
    label: 'Description',
    default: 'Fill out the form below and we\'ll get back to you as soon as possible.'
  },
  submitButtonText: {
    type: 'text',
    label: 'Submit Button Text',
    default: 'Send Message'
  },
  backgroundColor: {
    type: 'text',
    label: 'Background Color',
    default: '#ffffff'
  },
  textColor: {
    type: 'text',
    label: 'Text Color',
    default: '#333333'
  },
  padding: {
    type: 'text',
    label: 'Padding',
    default: '3rem 1rem'
  },
  maxWidth: {
    type: 'text',
    label: 'Max Width',
    default: '600px'
  }
};

export default ContactForm; 